<template>
  <v-container>
    <div class="yearly-summary">
      <v-row class="my-2">
        <v-col>
          <h2 class="text-no-wrap">
            登録・利用数推移
          </h2>
        </v-col>
        <v-col class="pl-0 d-flex justify-end align-center">
          <month-picker
            :selected-date="startDate"
            @on-month-changed="saveStartDate"
          />
          <span class="mx-1">
            〜
          </span>
          <month-picker
            :selected-date="endDate"
            @on-month-changed="saveEndDate"
          />
          <v-btn
            variant="flat"
            border="0"
            height="40"
            :color="color.mainColor()"
            :disabled="isInvalidDate"
            class="text-white font-weight-bold mx-2"
            @click="reload"
          >
            適用
          </v-btn>
          <v-btn
            variant="flat"
            prepend-icon="custom:download"
            height="40"
            border="md"
            @click="downloadCSV"
            class="text-glance-primary font-weight-bold border-current"
          >
            CSVダウンロード
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="font-size-075-rem text-color-light-gray w-100">
            ※表示・ダウンロードは最大1年分
          </div>
          <div class="font-size-075-rem text-color-light-gray w-100">
            ※月次報告の送付以降の数値を確定数値としてご利用ください。月内の利用数値は変動する場合があります。
          </div>
      </v-col>
      </v-row>

      <div
        v-if="isLoaded"
        class="mt-5">
        <div class="scroll-container">
          <div class="tables-wrapper">
            <v-table
              density="compact"
              class="mb-5">
              <yearly-summary-table-header
                :month-arr="monthArr"
                header-title="会員登録数"
              />
              <tbody class="text-center">
                <!-- 登録数:小児科経由 -->
                <yearly-summary-row
                  summary-type="syounika"
                  row-title="小児科"
                  :summary-list="syounikaRegistrationArr"
                  :summary-total="calcSum(syounikaRegistrationArr)"
                />
                <!-- 登録数:産婦人科経由 -->
                <yearly-summary-row
                  summary-type="obstetrics"
                  row-title="産婦人科"
                  :summary-list="obstetricsRegistrationArr"
                  :summary-total="calcSum(obstetricsRegistrationArr)"
                />
                <!-- 登録数:登録数合計 -->
                <yearly-summary-total-row
                  row-title="登録数合計"
                  :summary-list="allRegistrationArr"
                  :summary-total="calcSum(allRegistrationArr)"
                />
                <!-- 登録数:月末時点の累計登録数 -->
                <yearly-summary-total-row
                  row-title="月末時点の累計登録数"
                  :summary-list="cumulativeRegistrationArr"
                  summary-total="-"
                />
              </tbody>
            </v-table>
            <v-table
              density="compact"
              class="mb-5">
              <yearly-summary-table-header
                :month-arr="monthArr"
                header-title="オンライン相談利用数"
              />
              <tbody class="text-center">
                <!-- 利用数:小児科いつでも -->
                <yearly-summary-row
                  summary-type="syounika"
                  row-title="小児科いつでも"
                  :summary-list="syounikaAnytimeConsultationArr"
                  :summary-total="calcSum(syounikaAnytimeConsultationArr)"
                />
                <!-- 利用数:小児科夜間 -->
                <yearly-summary-row
                  summary-type="syounika"
                  row-title="小児科夜間"
                  :summary-list="syounikaReservationArr"
                  :summary-total="calcSum(syounikaReservationArr)"
                />
                <!-- 利用数:産婦人科いつでも -->
                <yearly-summary-row
                  summary-type="obstetrics"
                  row-title="産婦人科いつでも"
                  :summary-list="obstetricsAnytimeConsultationArr"
                  :summary-total="calcSum(obstetricsAnytimeConsultationArr)"
                />
                <!-- 利用数:産婦人科夜間 -->
                <yearly-summary-row
                  summary-type="obstetrics"
                  row-title="産婦人科夜間"
                  :summary-list="obstetricsReservationArr"
                  :summary-total="calcSum(obstetricsReservationArr)"
                />
                <!-- 利用数:産婦人科日中相談 -->
                <yearly-summary-row
                  v-if="corporation && corporation.midwifeAvailable"
                  summary-type="obstetrics"
                  row-title="産婦人科日中相談"
                  :summary-list="daytimeAdviceSheetArr"
                  :summary-total="calcSum(daytimeAdviceSheetArr)"
                />
                <!-- 利用数:利用数合計 -->
                <yearly-summary-total-row
                  row-title="利用数合計"
                  :summary-list="allUseArr"
                  :summary-total="calcSum(allUseArr)"
                />
              </tbody>
            </v-table>
            <v-table
              v-if="corporation && corporation.casesearchAvailable"
              density="compact"
              class="mb-5">
              <yearly-summary-table-header
                :month-arr="monthArr"
                header-title="みんなの相談検索"
              />
              <tbody class="text-center">
                <!-- みんなの相談検索:小児科閲覧数 -->
                <yearly-summary-row
                  summary-type="syounika"
                  row-title="小児科閲覧数"
                  :summary-list="syounikaCasesearchViewLogArr"
                  :summary-total="calcSum(syounikaCasesearchViewLogArr)"
                />
                <!-- みんなの相談検索:産婦人科閲覧数 -->
                <yearly-summary-row
                  summary-type="obstetrics"
                  row-title="産婦人科閲覧数"
                  :summary-list="obstetricsCasesearchViewLogArr"
                  :summary-total="calcSum(obstetricsCasesearchViewLogArr)"
                />
                <!-- みんなの相談検索:閲覧数合計 -->
                <yearly-summary-total-row
                  row-title="閲覧数合計"
                  :summary-list="allCasesearchViewLogArr"
                  :summary-total="calcSum(allCasesearchViewLogArr)"
                />
              </tbody>
            </v-table>
            <v-table
              v-if="corporation && corporation.medicineQuestionAvailable"
              density="compact"
              class="mb-5">
              <yearly-summary-table-header
                :month-arr="monthArr"
                header-title="くすりぼ"
              />
              <tbody class="text-center">
                <!-- くすりぼ:くすりぼ利用数 -->
                <yearly-summary-row
                  summary-type="medicineQuestion"
                  row-title="利用数"
                  :summary-list="medicineQuestionSelectionArr"
                  :summary-total="calcSum(medicineQuestionSelectionArr)"
                />
                </tbody>
            </v-table>
        </div>
        </div>
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
        style="height: 600px"
      >
        <img
          src="@/images/loading.gif"
          width="128"
          height="128"
        >
      </div>
    </div>
  </v-container>
</template>
<script lang="ts">
import { defineComponent, computed, onMounted, watch } from "vue"
import { YearlySummaryRow } from "../stores/store_types"
import { useStore } from "../stores/index"
import monthPicker from "./shared/MonthPicker.vue"
import { themeColor } from "../service/theme_color"
import yearlySummaryTableHeader from "./shared/YearlySummaryTableHeader.vue"
import yearlySummaryRow from "./shared/YearlySummaryRow.vue"
import yearlySummaryTotalRow from "./shared/YearlySummaryTotalRow.vue"

export default defineComponent({
  name: "YearlySummaryPage",
  components: {
    monthPicker,
    yearlySummaryTableHeader,
    yearlySummaryRow,
    yearlySummaryTotalRow
  },
  setup() {
    // vuex
    const store = useStore()
    // settings
    const color = themeColor()

    const corporation = computed(() => store.getters.corporation)
    const startDate = computed(() => new Date(store.getters.selectedYearlySummaryStartDate))
    const endDate = computed(() => new Date(store.getters.selectedYearlySummaryEndDate))
    const yearlySummaryRows = computed(() => store.getters.YearlySummaryRows)

    // 表を作成
    const isLoaded = computed(() => {
      return yearlySummaryRows.value instanceof Array && yearlySummaryRows.value.length > 0
    })
    const monthArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.month)
    })
    const syounikaRegistrationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.syounikaRegistration || 0)
    })
    const obstetricsRegistrationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.obstetricsRegistration || 0)
    })
    const allRegistrationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.allRegistration || 0)
    })
    const cumulativeRegistrationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.cumulativeRegistration || 0)
    })
    const syounikaReservationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.syounikaReservation || 0)
    })
    const obstetricsReservationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.obstetricsReservation || 0)
    })
    const syounikaAnytimeConsultationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.syounikaAnytimeConsultation || 0)
    })
    const obstetricsAnytimeConsultationArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.obstetricsAnytimeConsultation || 0)
    })
    const daytimeAdviceSheetArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.daytimeAdviceSheet || 0)
    })
    const medicineQuestionSelectionArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.medicineQuestionSelection || 0)
    })
    const allUseArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.allUse || 0)
    })
    // みんなの相談検索 小児科閲覧数
    const syounikaCasesearchViewLogArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.syounikaCasesearchViewLog || 0)
    })
    // みんなの相談検索 産婦人科閲覧数
    const obstetricsCasesearchViewLogArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.obstetricsCasesearchViewLog || 0)
    })
    // みんなの相談検索 閲覧数合計
    const allCasesearchViewLogArr = computed(() => {
      return yearlySummaryRows.value.map( (row:YearlySummaryRow) => row.allCasesearchViewLog || 0)
    })
    const isValidDate = computed(() => {
      if (!(startDate.value instanceof Date) || !(endDate.value instanceof Date)) {
        return false
      }
      if(startDate.value > endDate.value) {
        return false
      }
      if (diffMonth(startDate.value, endDate.value) > 12) {
        return false
      }
      return true
    })
    const diffMonth = (startDateValue: Date, endDateValue: Date) => {
      const startMonth = startDateValue.getFullYear() * 12 + startDateValue.getMonth()
      const endMonth = endDateValue.getFullYear() * 12 + endDateValue.getMonth()

      return (endMonth - startMonth) + 1
    }
    const isInvalidDate = computed(() => {
      return !(isValidDate.value)
    })

    const downloadCSV = async () => {
      await store.dispatch("downloadYearlySummary")
    }
    const reload = async () => {
      await store.dispatch("fetchYearlySummary")
    }
    const saveStartDate = async (selectedValue: Date) => {
      await store.dispatch("commitSelectedYearlySummaryStartDate", selectedValue)
    }
    const saveEndDate = async (selectedValue: Date) => {
      await store.dispatch("commitSelectedYearlySummaryEndDate", selectedValue)
    }
    const calcSum = (arr: Array<number>) => {
      return arr.reduce((a: number, b: number) => a + b)
    }

    onMounted(() => {
      store.dispatch("fetchYearlySummary")
    })

    // selectedCorporationIdがnullから設定された場合（ページリロード時のタイミング問題）に再取得する
    watch(
      () => store.getters.selectedCorporationId,
      (newId, oldId) => {
        if (oldId === null && newId !== null) {
          store.dispatch("fetchYearlySummary")
        }
      }
    )

    return {
      startDate,
      endDate,
      yearlySummaryRows,
      monthArr,
      syounikaRegistrationArr,
      obstetricsRegistrationArr,
      allRegistrationArr,
      cumulativeRegistrationArr,
      syounikaReservationArr,
      obstetricsReservationArr,
      syounikaAnytimeConsultationArr,
      obstetricsAnytimeConsultationArr,
      daytimeAdviceSheetArr,
      medicineQuestionSelectionArr,
      allUseArr,
      syounikaCasesearchViewLogArr,
      obstetricsCasesearchViewLogArr,
      allCasesearchViewLogArr,
      isLoaded,
      isValidDate,
      isInvalidDate,
      calcSum,
      downloadCSV,
      reload,
      saveStartDate,
      saveEndDate,
      corporation,
      color
    }
  }
})
</script>

<style lang="scss">
.yearly-summary{
  .v-table {
    th, td {
      border: 0 !important;
    }
  }

  .scroll-container {
    overflow-x: auto;
    width: 100%;
  }

  .tables-wrapper {
    min-width: max-content;
    display: inline-block; /* 横幅を内容に合わせて拡張 */
    :deep(table) {
      width: auto;
      table-layout: fixed;
    }
  }
}
</style>
